import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { EditorContent, EditorContext } from "@tiptap/react"

import { useEditorInstance } from "@/tiptap/hooks/use-editor-instance"
import { useUiEditorState } from "@/tiptap/hooks/use-ui-editor-state"
import { useScrollToHash } from "@/tiptap/ui/copy-anchor-link-button/use-scroll-to-hash"

import { AppProvider } from "@/tiptap/contexts/app-context"
import {
  TocProvider,
  useToc,
} from "@/tiptap/nodes/toc-node/context/toc-context"
import { TocSidebar } from "@/tiptap/nodes/toc-node"

import { TableHandle } from "@/tiptap/nodes/table-node/ui/table-handle/table-handle"
import { TableSelectionOverlay } from "@/tiptap/nodes/table-node/ui/table-selection-overlay"
import { TableCellHandleMenu } from "@/tiptap/nodes/table-node/ui/table-cell-handle-menu"
import { TableExtendRowColumnButtons } from "@/tiptap/nodes/table-node/ui/table-extend-row-column-button"

import { EmojiDropdownMenu } from "@/tiptap/ui/emoji-dropdown-menu"
// import { MentionDropdownMenu } from "@/tiptap/ui/mention-dropdown-menu"
import { SlashDropdownMenu } from "@/tiptap/ui/slash-dropdown-menu"
import { DragContextMenu } from "@/tiptap/ui/drag-context-menu"

import { EditorToolbar, EditorOverlayToolbars } from "@/tiptap/shell/toolbar"
import { SourceModeModal } from "@/tiptap/shell/source-mode-modal"
import { ThemeToggle } from "@/tiptap/templates/notion-like/notion-like-editor-theme-toggle"

import { fullMode } from "@/tiptap/modes/full-mode"
import { readToolbarPreference, withToolbarPreference } from "@/tiptap/modes/toolbar-preference"

/**
 * Editor content area with dropdown menus and floating UI.
 * Reads the editor from EditorContext.
 */
const ShellEditorContent = memo(function ShellEditorContent({ modeConfig }) {
  const { editor } = useContext(EditorContext)
  const { isDragging } = useUiEditorState(editor)

  useScrollToHash({ topOffset: 104 })

  if (!editor) return null

  return (
    <EditorContent
      editor={editor}
      role="presentation"
      className={modeConfig.layout.contentClassName}
      style={{ cursor: isDragging ? "grabbing" : "auto" }}
    >
      <DragContextMenu />
      <EmojiDropdownMenu />
      {/* <MentionDropdownMenu /> */}
      <SlashDropdownMenu />
      <EditorOverlayToolbars modeConfig={modeConfig} />
    </EditorContent>
  )
})

/**
 * Inner shell that creates the editor instance and composes UI.
 * Separated from providers so it can access TocContext.
 */
function ShellInner({ content, placeholder, onChange, onReady, recordType, recordId, modeConfig }) {
  const { setTocContent } = useToc()
  const [sourceOpen, setSourceOpen] = useState(false)

  const resolvedPlaceholder = useMemo(
    () => placeholder ?? modeConfig.editor.placeholder,
    [placeholder, modeConfig.editor.placeholder],
  )

  const editor = useEditorInstance({
    content,
    placeholder: resolvedPlaceholder,
    onChange,
    onTocUpdate: setTocContent,
    recordType,
    recordId,
    editorClassName: modeConfig.editor.editorClassName,
  })
  const editorContextValue = useMemo(() => ({ editor }), [editor])

  const handleToggleSource = useCallback(() => {
    setSourceOpen((prev) => !prev)
  }, [])
  const handleCloseSource = useCallback(() => {
    setSourceOpen(false)
  }, [])
  const renderTableCellMenu = useCallback(
    (props) => (
      <TableCellHandleMenu
        editor={props.editor}
        onMouseDown={(event) => props.onResizeStart?.("br")(event)}
      />
    ),
    [],
  )

  useEffect(() => {
    if (editor) {
      onReady?.(editor)
    }
  }, [editor, onReady])

  if (!editor) {
    return <div style={{ padding: "1rem" }}>Loading editor...</div>
  }

  return (
    <div className={modeConfig.layout.wrapperClassName}>
      <ThemeToggle />
      <EditorContext.Provider value={editorContextValue}>
        <EditorToolbar
          modeConfig={modeConfig}
          onToggleSource={handleToggleSource}
        />

        <div className={modeConfig.layout.layoutClassName}>
          <ShellEditorContent modeConfig={modeConfig} />
          {modeConfig.layout.showTocSidebar && <TocSidebar topOffset={104} />}
        </div>

        {modeConfig.layout.showTableHandles && (
          <>
            <TableExtendRowColumnButtons />
            <TableHandle />
            <TableSelectionOverlay
              showResizeHandles={true}
              cellMenu={renderTableCellMenu}
            />
          </>
        )}
      </EditorContext.Provider>

      {modeConfig.toolbar.showSourceMode && (
        <SourceModeModal
          editor={editor}
          isOpen={sourceOpen}
          onClose={handleCloseSource}
        />
      )}
    </div>
  )
}

/**
 * Reusable Tiptap editor shell.
 *
 * Supports both full-page and inline editors via the `mode` prop.
 * All editor creation, toolbar composition, and provider wiring is
 * handled internally -- callers only pass content and an onChange callback.
 *
 * @param {Object} props
 * @param {string}  props.content      - Initial HTML content
 * @param {string}  [props.placeholder] - Override mode default placeholder
 * @param {Function} [props.onChange]   - Called with serialized HTML on change
 * @param {string}  [props.recordType]  - Owning record type used to tag image uploads
 * @param {string}  [props.recordId]    - Owning record id used to tag image uploads
 * @param {Object}  [props.mode]       - Mode config object (defaults to fullMode)
 */
export function TiptapEditorShell({
  content,
  placeholder,
  onChange,
  onReady,
  recordType,
  recordId,
  mode = fullMode,
}) {
  const modeConfig = useMemo(
    () => withToolbarPreference(mode, readToolbarPreference()),
    [mode],
  )

  return (
    <AppProvider>
      <TocProvider>
        <ShellInner
          content={content}
          placeholder={placeholder}
          onChange={onChange}
          onReady={onReady}
          recordType={recordType}
          recordId={recordId}
          modeConfig={modeConfig}
        />
      </TocProvider>
    </AppProvider>
  )
}

export default TiptapEditorShell
